import React, { useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Box,
   Typography,
   TextField,
   Button,
   IconButton,
   CircularProgress,
   Snackbar,
   Alert,
   Link,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getAuth, signInWithCustomToken } from 'firebase/auth';

interface EventSignupModalProps {
   open: boolean;
   onClose: () => void;
   eventId: string;
   onSignupSuccess?: (user: any) => void;
   isAlbum?: boolean;
   isPaidAlbum?: boolean;
   albumPaymentInfo?: {
      pricingModel: 'one_time' | 'per_photo';
      pricePerPhoto?: number;
      albumTitle?: string;
   };
}

const EventSignupModal: React.FC<EventSignupModalProps> = ({
   open,
   onClose,
   eventId,
   onSignupSuccess,
   isAlbum = false,
   isPaidAlbum = false,
   albumPaymentInfo,
}) => {
   const auth = getAuth();

   const [fullName, setFullName] = useState('');
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

   const handleRegistration = async () => {
      // Validation
      if (!fullName.trim() || !email.trim() || !password.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password.length < 6) {
         setSnackbarMessage('Password must be at least 6 characters');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         console.log(`📤 ${isAlbum ? 'Album' : 'Event'} signup data:`, { eventId, fullName, email });

         // Register user via event or album-specific endpoint
         const endpoint = isAlbum
            ? `${process.env.REACT_APP_API_URL}/api/auth/register-for-album`
            : `${process.env.REACT_APP_API_URL}/api/auth/register-for-event`;

         const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               fullName,
               email,
               password,
               ...(isAlbum ? { albumId: eventId } : { eventId }),
            }),
         });

         const result = await response.json();

         if (result.success) {
            console.log('📋 Registration result:', result);
            console.log('📋 Album access granted?', result.albumAccessGranted);
            console.log('📋 Auto-approved?', result.autoApproved);

            const entityType = isAlbum ? 'album' : 'event';
            let message = '';

            if (isAlbum && result.autoApproved) {
               message =
                  'Account created successfully! You have been added to the album as a participant.';
            } else if (result.waitlistCreated || result.albumAccessGranted) {
               message = `Account created successfully! You have been added to the ${entityType} waitlist.`;
            } else {
               message = 'Account created successfully! Redirecting to dashboard...';
            }

            setSnackbarMessage(message);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Log in the user with custom token
            if (result.token) {
               try {
                  const userCredential = await signInWithCustomToken(auth, result.token);
                  console.log('✅ User logged in successfully:', userCredential.user.uid);

                  if (result.autoApproved) {
                     console.log('✅ User automatically added as album participant');
                  } else if (result.waitlistCreated || result.albumAccessGranted) {
                     console.log('✅ User automatically added to waitlist');
                  } else {
                     console.warn('⚠️ User was NOT added to waitlist or as participant');
                  }

                  // Wait for auth state to fully update
                  await new Promise((resolve) => setTimeout(resolve, 500));

                  // Close modal
                  onClose();

                  // Check if this is a paid album requiring payment
                  if (isPaidAlbum && albumPaymentInfo) {
                     console.log('💳 Paid album detected - pricing model:', albumPaymentInfo.pricingModel);
                     
                     // For per-photo albums, just redirect to feed (no upfront payment)
                     if (albumPaymentInfo.pricingModel === 'per_photo') {
                        console.log('📸 Per-photo album - redirecting to feed without payment');
                        
                        // Call success callback (parent will redirect to feed in album flow)
                        if (onSignupSuccess && result.user) {
                           onSignupSuccess({
                              ...result.user,
                              autoApproved: result.autoApproved,
                              isDirectMember: result.isDirectMember,
                           });
                           return;
                        }
                        // Fallback for non-album (e.g. event) flow
                        window.location.href = '/feed';
                        return;
                     }
                     
                     // For one-time albums, create payment session and redirect to Stripe
                     if (albumPaymentInfo.pricingModel === 'one_time') {
                        console.log('💰 One-time payment album - creating payment session...');
                        
                        try {
                           // Get fresh auth token
                           const idToken = await userCredential.user.getIdToken();
                           
                           // Create payment session directly
                           const paymentResponse = await fetch(
                              `${process.env.REACT_APP_API_URL}/api/albums/${eventId}/create-payment-session`,
                              {
                                 method: 'POST',
                                 headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${idToken}`,
                                 },
                                 body: JSON.stringify({
                                    userId: userCredential.user.uid,
                                    email: userCredential.user.email || email,
                                    albumTitle: albumPaymentInfo.albumTitle || `Album ${eventId}`,
                                    albumId: eventId,
                                    amount: albumPaymentInfo.pricePerPhoto || 5.0,
                                    pricingModel: 'one_time',
                                    returnUrl: `${window.location.origin}/feed`,
                                    cancelUrl: `${window.location.origin}/albums/public/${eventId}`,
                                 }),
                              }
                           );

                           const paymentResult = await paymentResponse.json();

                           if (paymentResponse.ok && paymentResult.sessionUrl) {
                              console.log('✅ Payment session created! Redirecting to Stripe...');
                              
                              // Store payment intent
                              localStorage.setItem('pendingAlbumAccess', JSON.stringify({
                                 albumId: eventId,
                                 albumTitle: albumPaymentInfo.albumTitle || `Album ${eventId}`,
                                 paymentType: 'one_time',
                                 timestamp: Date.now(),
                              }));
                              
                              // Redirect to Stripe checkout
                              window.location.href = paymentResult.sessionUrl;
                              return;
                           } else {
                              console.error('❌ Failed to create payment session:', paymentResult);
                              setSnackbarMessage('Failed to create payment session. Please try again.');
                              setSnackbarSeverity('error');
                              setSnackbarOpen(true);
                           }
                        } catch (paymentError) {
                           console.error('❌ Error creating payment session:', paymentError);
                           setSnackbarMessage('Payment setup failed. Please try again from the album page.');
                           setSnackbarSeverity('error');
                           setSnackbarOpen(true);
                        }
                        
                        // Fallback: redirect to album page
                        setTimeout(() => {
                           window.location.href = `/albums/public/${eventId}`;
                        }, 2000);
                        return;
                     }
                  }

                  // For non-paid albums, call success callback (parent will redirect to feed in album flow)
                  if (onSignupSuccess && result.user) {
                     onSignupSuccess({
                        ...result.user,
                        autoApproved: result.autoApproved,
                        isDirectMember: result.isDirectMember,
                     });
                     return;
                  }
                  // Fallback for non-album (e.g. event) flow
                  console.log('🔄 Redirecting to feed page...');
                  window.location.href = '/feed';
               } catch (authError) {
                  console.error('❌ Error signing in with custom token:', authError);
                  setSnackbarMessage('Login failed. Please try signing in manually.');
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
               }
            } else {
               // No token provided, just close
               setTimeout(() => {
                  onClose();
                  setSnackbarMessage('Registration successful! Please sign in.');
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
               }, 1500);
            }
         } else {
            setSnackbarMessage(result.error || 'Registration failed. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error registering for event:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleClose = () => {
      if (!submitting) {
         onClose();
      }
   };

   return (
      <>
         <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>Join {isAlbum ? 'Album' : 'Event'}</Typography>
                  <IconButton onClick={handleClose} disabled={submitting}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Create an account to request access to this {isAlbum ? 'album' : 'event'}.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
                  helperText='At least 6 characters'
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />

               {/* Sign In Link */}
               <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     Already have an account?{' '}
                     <Link
                        href={`/login?returnUrl=${encodeURIComponent('/feed')}`}
                        underline='hover'
                        sx={{ color: '#01949A', fontWeight: 600 }}
                     >
                        Sign In
                     </Link>
                  </Typography>
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleClose} disabled={submitting}>
                  Cancel
               </Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Create Account & Join'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar for notifications */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default EventSignupModal;
